import { Injectable } from '@angular/core';
import { firstValueFrom, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  public StringToDate(str: string): Date {
    let regex = /(\d*)\/(\d*)\/(\d*)/gi
    let match = regex.exec(str)
    if (!match) return null
    let year = parseInt(match[3])
    let month = parseInt(match[2]) - 1
    let day = parseInt(match[1])
    return new Date(year, month, day)
  }

  public DateToString(date: Date): string {
    if (!date) return null
    let year = date.getFullYear()
    let month = date.getMonth() + 1
    let day = date.getDate()
    return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year.toString().padStart(4, '0')}`
  }

  public StringToTime(str: string): Date {
    let regex = /(\d*):(\d*)(:(\d*))?/gi
    let match = regex.exec(str)
    if (!match) return null

    let hour = 0
    let minute = 0
    let second = 0

    if (match[1] != null) hour = parseInt(match[1])
    if (match[2] != null) minute = parseInt(match[2])
    if (match[4] != null) second = parseInt(match[4])
    return new Date(1900, 0, 1, hour, minute, second)
  }

  public ShortTimeToString(time: Date): string {
    if (!time) return null
    let hour = time.getHours()
    let minute = time.getMinutes()
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
  }

  public TimeToString(time: Date): string {
    if (!time) return null
    let hour = time.getHours()
    let minute = time.getMinutes()
    let second = time.getSeconds()
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}${second != null && second != 0 ? `:${second.toString().padStart(2, '0')}` : ''}`
  }

  public DateToISO8601(date: Date): string {
    if (!date) return null
    let year = date.getFullYear()
    let month = date.getMonth() + 1
    let day = date.getDate()
    let hour = date.getHours()
    let minute = date.getMinutes()
    let second = date.getSeconds()
    let datePart = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    let timePart = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}${second != null && second != 0 ? `:${second.toString().padStart(2, '0')}` : ''}`
    return `${datePart}T${timePart}`
  }
  
  public StringToDateTime(str: string): Date {
    let regex = /(\d*)\/(\d*)\/(\d*)(\s*(\d*):(\d*)(:(\d*))?)?/gi
    let match = regex.exec(str)
    if (!match) return null

    let year = parseInt(match[3])
    let month = parseInt(match[2]) - 1
    let day = parseInt(match[1])

    let hour = 0
    let minute = 0
    let second = 0

    if (match[5] != null) hour = parseInt(match[5])
    if (match[6] != null) minute = parseInt(match[6])
    if (match[8] != null) second = parseInt(match[8])

    return new Date(year, month, day, hour, minute, second)

  }

  public DateTimeToString(date: Date): string {
    if (!date) return null
    let year = date.getFullYear()
    let month = date.getMonth() + 1
    let day = date.getDate()
    let hour = date.getHours()
    let minute = date.getMinutes()
    let second = date.getSeconds()
    let datePart = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year.toString().padStart(4, '0')}`
    let timePart = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}${second != null && second != 0 ? `:${second.toString().padStart(2, '0')}` : ''}`
    return `${datePart} ${timePart}`
  }

  public Sleep(milliseconds: number): Promise<0> {
    return firstValueFrom(timer(milliseconds))
  }

  public async SHA256FromBase64Data(data: string): Promise<string> {
    const binaryData = Uint8Array.from(atob(data), c => c.charCodeAt(0))
    const hashBuffer = await crypto.subtle.digest('SHA-256', binaryData)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const sha256Hash = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('')
    return sha256Hash
  }

  public ElementoVisibleEnPantalla(elemento: HTMLElement): Promise<boolean> {
    return new Promise(function(resolve, reject) {
      var options = {
        root: null, // Utiliza el viewport como contenedor de desplazamiento
        rootMargin: '0px', // Margen adicional alrededor del viewport
        threshold: 1.0 // Porcentaje de visibilidad del elemento requerido para activar la devolución de llamada
      };

      var observer = new IntersectionObserver(function(entries, observer) {
        entries.forEach(function(entry) {
          // Verifica si el elemento objetivo está completamente contenido y visible en el viewport
          if (entry.isIntersecting && entry.intersectionRatio === 1) {
            // Detiene la observación
            observer.unobserve(entry.target);
            resolve(true); // Resuelve la promesa indicando que el elemento está visible
          } else {
            observer.unobserve(entry.target);
            resolve(false); // Resuelve la promesa indicando que el elemento no está visible
          }
        });
      }, options);

      // Observa el elemento
      observer.observe(elemento);
    });
  }

  public InjectScript(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = url
      script.onload = () => {
        resolve()
      }
      script.onerror = (e) => {
        reject(e)
      }
      document.body.append(script)
    })
  }

  public toDMS(coordinate: number): number[][] {
    let absCoordinate = Math.abs(coordinate);
    let degrees = Math.floor(absCoordinate);
    let minutes = Math.floor((absCoordinate - degrees) * 60);
    let seconds = (absCoordinate - degrees - minutes / 60) * 3600;
    return [
      [degrees, 1],
      [minutes, 1],
      [Math.round(seconds * 100), 100]
    ];
  }

  public GetFileExtension(fileName: string): string {
    if (fileName.indexOf('.') != -1) {
      return fileName.substring(fileName.lastIndexOf('.'), fileName.length)
    } else {
      return ''
    }
  }

  public createWhiteImage(width: number, height: number, newImageBackground?: string): string {
    if (newImageBackground == null) newImageBackground = 'white';
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    if (context) {
      context.fillStyle = newImageBackground
      context.fillRect(0, 0, width, height)
    } else {
      throw new Error('No se pudo obtener el contexto 2D.')
    }

    const dataURL = canvas.toDataURL('image/png')

    return dataURL
  }

  public DownloadJson(filename: string, data: object): void {
    const jsonStr = JSON.stringify(data, null, 2); // con indentación bonita
    const blob = new Blob([jsonStr], { type: "application/json" });
    this.DownloadBlob(filename.endsWith(".json") ? filename : `${filename}.json`, blob);
  }

  public DownloadBlob(filename: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public SelectFileAndReadJson(): Promise<any> {
    return new Promise((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "application/json";

      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) {
          reject(new Error("No se seleccionó ningún fichero"));
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          try {
            const json = JSON.parse(reader.result as string);
            resolve(json);
          } catch (err) {
            reject(new Error("El fichero no contiene un JSON válido"));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      };

      input.click();
    });
  }

}
